import { AuthConfig as LibAuthConfig } from 'angular-oauth2-oidc';
import { LoadedUser } from './models/user';

export type IsAuthzAppUserPredicate<A = unknown> = (user: LoadedUser & A) => boolean;

export class AuthConfig extends LibAuthConfig {
  constructor(json?: Partial<AuthConfig>) {
    super(json);
  }

  /**
   * Url of a userinfo endpoint in your application backend
   *
   * When supplied, this endpoint will be called after the user has been authenticated
   * The attributes of the object returned will be used to enrich the user object with
   * information that is also returned by the OpenId Connect userinfo endpoint.
   *
   * @see DefaultUserInfoService
   */
  appUserInfoEndpoint?: string;

  /**
   * The client-side route to redirect to when the mrm client id selection is required
   *
   * @default 'client-selection'
   */
  clientSelectionRoute?: string;

  /**
   * Function that will be supplied a reference to the fully _loaded_ authenticated user
   * object and should return whether or not that the user is authorized to use the
   * application.
   *
   * If not supplied, will assume that all authenticated users are also authorized
   *
   * @see AuthzAppUserGuard
   * @see AuthService.isAuthorizedAppUser$
   */
  getIsAuthorizedAppUser?: IsAuthzAppUserPredicate;

  /**
   * The client-side route to redirect to when the user is found to be unauthorized
   *
   * @default 'unauthorized'
   */
  unauthorizedRoute?: string;
  /**
   * Reload the app user attributes when impersonating another client?
   *
   * Causes the `userLoaded$` observable on the `AuthService` to emit the latest "live" values
   * returned from `appUserInfoEndpoint` REST api whenever the `AuthService.impersonateClient`
   * method is used to switch client id.
   * @default false
   * */
  reloadAppUserInfoOnClientImpersonation?: boolean;

  /**
   * When true, the library manages MRI client ID selection for the application:
   * - `ClientSelectionGuard` enforces client-id validation on every navigation and redirects to `clientSelectionRoute`
   *   when no valid client is established.
   * - `AuthService` auto-detects a client ID from the URL (using `mriClientIdQueryParamName`) at startup, before the
   *   store rehydrates.
   * - The `appUserInfoEndpoint` request is deferred until `isSignedInClientValid$` emits `true`, ensuring the request
   *   carries a valid client-id header and `userLoaded$` does not emit until client selection is complete.
   *
   * Set to `false` (default) when the application does not use the library's client selection flow — for example,
   * apps that perform no multi-client selection or that manage client switching entirely outside the library.
   *
   * @default false
   */
  manageClientIdSelection?: boolean;

  /**
   * The name of the cookie that is set upon validating that the signed in client id is assigned to the user. The value
   * of this cookie will be sent to the client selection route as the query parameter named `lastValidatedClientId`
   * @default 'lastValidatedClientId'
   */
  validatedClientCookieName?: string;

  /**
   * The name of the URL query parameter that carries the MRI client ID. Used when `manageClientIdSelection` is true
   * to auto-detect the client ID from the current browser URL at startup and to read/write it during client selection
   * navigation.
   * @default 'mriclientid'
   */
  mriClientIdQueryParamName?: string;
}

/** The `AuthConfig` fields that are guaranteed non-optional on `ResolvedAuthConfig`. */
export type ResolvedAuthConfigFields = Required<
  Pick<
    AuthConfig,
    | 'clearHashAfterLogin'
    | 'clientSelectionRoute'
    | 'mriClientIdQueryParamName'
    | 'sessionChecksEnabled'
    | 'validatedClientCookieName'
  >
>;

/**
 * Narrows `AuthConfig` to the shape returned by `AuthService` after defaults have been applied.
 * Fields listed here are guaranteed non-optional at runtime even though they are optional on the
 * input `AuthConfig`.
 */
export type ResolvedAuthConfig = Readonly<AuthConfig & ResolvedAuthConfigFields>;

export function resolveAuthConfig(value: AuthConfig): ResolvedAuthConfig {
  const resolvedFields: ResolvedAuthConfigFields = {
    clearHashAfterLogin: false, // https://github.com/manfredsteyer/angular-oauth2-oidc/issues/457#issuecomment-431807040
    clientSelectionRoute: value.clientSelectionRoute ?? 'client-selection',
    mriClientIdQueryParamName: value.mriClientIdQueryParamName ?? 'mriclientid',
    sessionChecksEnabled: false, // disabled by default
    validatedClientCookieName: value.validatedClientCookieName ?? 'lastValidatedClientId'
  };

  const postLogoutRedirectUri =
    value.postLogoutRedirectUri || (value.issuer ? new URL(value.issuer).origin + '/logout/redirect' : undefined);

  const config = new AuthConfig({
    ...value,
    ...resolvedFields,
    ...(postLogoutRedirectUri ? { postLogoutRedirectUri } : {}),
    // turn off checks as urls in discovery document returned by proxy will not match expected urls based on issuer value
    strictDiscoveryDocumentValidation: false,
    skipIssuerCheck: true,
    useIdTokenHintForSilentRefresh: true,
    responseType: 'code'
  });

  return config as ResolvedAuthConfig;
}
